import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { E_FORCE_DIALOG_CLOSE, E_GET_BP, E_GET_BP_DETAIL, E_GET_DIALOG, emitter } from '../../event';
import './assets/style.less';

type BpPayload = {
    show?: boolean;
    dialogid?: number;
    style?: number;
    caption?: string;
    text?: string;
    button1?: string;
    button2?: string;
    days?: number;
    level?: number;
    exp?: number;
    need?: number;
};

type BpDetailPayload = {
    index?: number;
    text?: string;
    openPass?: boolean;
    rewardType?: number;
    rewardDetails?: number;
    previewSkinId?: number;
};

type DetailSegment = {
    color: string;
    text: string;
};

type DetailLine = {
    isEmpty: boolean;
    segments: DetailSegment[];
};

type RewardState = 'claim' | 'done' | 'vip' | 'open' | 'locked';

type BpReward = {
    index: number;
    level: number;
    title: string;
    status: string;
    points: string;
    current: number;
    need: number;
    percent: number;
    state: RewardState;
};

type RewardMeta = {
    rewardType: number;
    rewardDetails: number;
    previewSkinId?: number;
};

const ITEMS_PER_PAGE = 5;

const importAll = (r: any) => {
    const images: Record<string, string> = {};
    r.keys().forEach((item: string) => {
        const module = r(item);
        images[item.replace('./', '')] = module.default || module;
    });
    return images;
};

const bpImages = importAll(require.context('./assets/icons', false, /\.(png|jpe?g|webp|svg)$/));
const inventoryImages = importAll(require.context('../inventory/assets/icons', false, /\.(png|jpe?g|webp|svg)$/));
const inventorySkinImages = importAll(require.context('../inventory/assets/skins', false, /\.(png|jpe?g|webp|svg)$/));

const stripColorCodes = (value: string) => value.replace(/\{[0-9A-Fa-f]{6}\}/g, '').trim();
const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));
const normalizeRewardTitle = (value: string) => {
    return stripColorCodes(value)
        .toLowerCase()
        .replace(/ё/g, 'е')
        .replace(/[«»"“”]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
};
const titleFromCaption = (caption: string) => {
    const clean = stripColorCodes(caption);
    const splitIndex = clean.indexOf('|');
    if (splitIndex > 0) return clean.slice(0, splitIndex).trim();
    return clean;
};

const parsePayload = <T,>(payload: any): T | null => {
    try {
        const raw = Array.isArray(payload) ? payload[0] : payload;
        if (typeof raw === 'string') return JSON.parse(raw) as T;
        if (raw && typeof raw === 'object') return raw as T;
        return null;
    } catch {
        return null;
    }
};

const normalizeDetailMarkup = (value: string) => {
    return value
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<tbl>/gi, ' ')
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
};

const DETAIL_HIDDEN_PREFIXES = [
    'уровень:',
    'награда за прохождение:',
    'полученные очки:',
];
const DEFAULT_DETAIL_COLOR = '#f2f2f2';

const filterDetailText = (value: string) => {
    const filtered = normalizeDetailMarkup(value)
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => !DETAIL_HIDDEN_PREFIXES.some((prefix) => stripColorCodes(line).toLowerCase().startsWith(prefix)))
        .join('\n')
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n');

    return normalizeDetailMarkup(filtered);
};

const detectPassRequirement = (value: string) => {
    const lines = normalizeDetailMarkup(value)
        .split('\n')
        .map((line) => stripColorCodes(line).trim().toLowerCase())
        .filter((line) => line.length > 0);

    const statusLine = lines.find((line) => line.startsWith('статус:'));
    if (statusLine) return statusLine.includes('advance pass');

    return lines.some((line) => line.includes('обладателям advance pass'));
};

const parseDetailLines = (value: string): DetailLine[] => {
    return value.split('\n').map((line) => {
        if (!line.trim()) {
            return { isEmpty: true, segments: [] };
        }

        const segments: DetailSegment[] = [];
        const colorPattern = /\{([0-9A-Fa-f]{6})\}/g;
        let currentColor = DEFAULT_DETAIL_COLOR;
        let lastIndex = 0;
        let match: RegExpExecArray | null;

        while ((match = colorPattern.exec(line)) !== null) {
            if (match.index > lastIndex) {
                segments.push({
                    color: currentColor,
                    text: line.slice(lastIndex, match.index),
                });
            }
            currentColor = `#${match[1]}`;
            lastIndex = match.index + match[0].length;
        }

        if (lastIndex < line.length) {
            segments.push({
                color: currentColor,
                text: line.slice(lastIndex),
            });
        }

        return {
            isEmpty: false,
            segments,
        };
    });
};

const parseProgress = (value: string) => {
    const match = value.match(/(\d+)\D+(\d+)/);
    if (!match) return { current: 0, need: 0 };
    return {
        current: Number(match[1]),
        need: Number(match[2]),
    };
};

const detectState = (statusRaw: string, statusClean: string): RewardState => {
    const lowerRaw = statusRaw.toLowerCase();
    const lower = statusClean.toLowerCase();

    if (lowerRaw.includes('{d966ff}') || lower.includes('получ') || lower.includes('claim')) return 'claim';
    if (lower.includes('пройд') || lower.includes('done') || lower.includes('claimed')) return 'done';
    if (lower.includes('advance pass') || lower.includes('pass')) return 'vip';
    if (lower.includes('откры') || lower.includes('доступ') || lower.includes('open')) return 'open';
    if (lower.includes('lock')) return 'locked';
    return 'locked';
};

const normalizeStatusLabel = (status: string, state: RewardState) => {
    const lower = status.toLowerCase();
    if (lower === 'claim') return 'ПОЛУЧИТЕ НАГРАДУ';
    if (lower === 'done' || lower === 'claimed') return 'ПРОЙДЕН';
    if (lower === 'pass' || lower === 'vip') return 'ADV PASS';
    if (lower === 'open') return 'ОТКРЫТО';
    if (lower === 'lock' || lower === 'locked') return 'ЗАКРЫТО';
    if (state === 'locked' && status.length === 0) return 'ЗАКРЫТО';
    return status;
};

const parseRewards = (text: string): BpReward[] => {
    const lines = text.split('<br>').map((line) => line.trim()).filter((line) => line.length > 0);
    const rewards: BpReward[] = [];

    lines.forEach((line) => {
        if (!line.includes('<tbl>')) return;

        const cols = line.split('<tbl>');
        if (cols.length < 3) return;

        const levelRaw = stripColorCodes(cols[0]);
        const statusRaw = cols[1];
        const status = stripColorCodes(statusRaw);
        const points = stripColorCodes(cols[2]);
        const levelMatch = levelRaw.match(/^(\d+)\.\s*(.*)$/);
        if (!levelMatch) return;

        const level = Number(levelMatch[1]);
        if (!Number.isFinite(level) || level < 1) return;

        const title = (levelMatch[2] || '').trim();
        const parsed = parseProgress(points);
        const percent = parsed.need > 0 ? Math.max(0, Math.min(100, Math.round((parsed.current / parsed.need) * 100))) : 0;
        const state = detectState(statusRaw, status);
        const statusLabel = normalizeStatusLabel(status, state);

        rewards.push({
            index: rewards.length,
            level,
            title,
            status: statusLabel,
            points,
            current: parsed.current,
            need: parsed.need,
            percent,
            state,
        });
    });

    return rewards;
};

const parseDaysFromCaption = (caption: string): number => {
    const clean = stripColorCodes(caption);
    const match = clean.match(/(\d+)\s*$/);
    return match ? Number(match[1]) : 0;
};

const SHARED_REWARD_IMAGES: Record<number, string> = {
    1: 'money.webp',
    3: 'lotocoins.webp',
    4: 'drugs.webp',
    8: 'rubles.webp',
    9: 'ammo.webp',
    10: 'metal.webp',
    14: 'status.webp',
    45: 'advancecredits.webp',
};

const getSharedRewardImage = (reward: BpReward, meta?: RewardMeta) => {
    const byType = meta ? SHARED_REWARD_IMAGES[meta.rewardType] : undefined;
    if (byType && bpImages[byType]) return bpImages[byType];

    const normalizedTitle = normalizeRewardTitle(reward.title);

    if (normalizedTitle.includes('advance credits') && bpImages['advancecredits.webp']) return bpImages['advancecredits.webp'];
    if (normalizedTitle.includes('advance status') && bpImages['status.webp']) return bpImages['status.webp'];
    if (normalizedTitle.includes('наличн') && bpImages['money.webp']) return bpImages['money.webp'];
    if ((normalizedTitle.includes('донат-счет') || normalizedTitle.includes('донат счет') || normalizedTitle.includes('кешбек')) && bpImages['rubles.webp']) {
        return bpImages['rubles.webp'];
    }
    if (normalizedTitle.includes('монет') && normalizedTitle.includes('лотер') && bpImages['lotocoins.webp']) return bpImages['lotocoins.webp'];
    if (normalizedTitle.includes('патрон') && bpImages['ammo.webp']) return bpImages['ammo.webp'];
    if (normalizedTitle.includes('наркот') && bpImages['drugs.webp']) return bpImages['drugs.webp'];
    if (normalizedTitle.includes('металл') && bpImages['metal.webp']) return bpImages['metal.webp'];

    return '';
};

const getAccessoryRewardImage = (meta?: RewardMeta) => {
    if (!meta || meta.rewardType !== 2 || meta.rewardDetails <= 0) return '';

    const key = `acs_${meta.rewardDetails}.webp`;
    return inventoryImages[key] || '';
};

const getSkinRewardImage = (meta?: RewardMeta) => {
    if (!meta || meta.rewardType !== 6 || !meta.previewSkinId || meta.previewSkinId <= 0) return '';

    const key = `${meta.previewSkinId}.png`;
    return inventorySkinImages[key] || '';
};

const getCardImage = (reward: BpReward, meta?: RewardMeta) => {
    const skinImage = getSkinRewardImage(meta);
    if (skinImage) return skinImage;

    const accessoryImage = getAccessoryRewardImage(meta);
    if (accessoryImage) return accessoryImage;

    const sharedImage = getSharedRewardImage(reward, meta);
    if (sharedImage) return sharedImage;

    const exact = bpImages[`bp_${reward.level}.webp`];
    if (exact) return exact;

    return bpImages['fallback.webp'] || '';
};

const getPrimaryLabel = (state: RewardState, fallback: string) => {
    if (state === 'claim') return 'ПОЛУЧИТЬ';
    if (state === 'done') return 'ПОДРОБНЕЕ';
    if (state === 'vip') return 'ПОДРОБНЕЕ';
    if (state === 'open') return 'ПОДРОБНЕЕ';
    if (fallback.length > 0) return fallback.toUpperCase();
    return 'ПОДРОБНЕЕ';
};

const getStateBadge = (state: RewardState, status: string) => {
    if (state === 'claim') return 'ДОСТУПНО';
    if (state === 'done') return 'ПРОЙДЕНО';
    if (state === 'vip') return 'ДОСТУПНО С ADVANCE PASS';
    if (state === 'open') return 'ОТКРЫТО';
    if (status.length > 0) return status;
    return 'ЗАКРЫТО';
};

const BattlePass = () => {
    const [visible, setVisible] = useState(false);
    const [caption, setCaption] = useState('BATTLE PASS');
    const [daysLeft, setDaysLeft] = useState(0);
    const [button1, setButton1] = useState('ВЫБРАТЬ');
    const [button2, setButton2] = useState('ЗАКРЫТЬ');
    const [rewards, setRewards] = useState<BpReward[]>([]);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [activePage, setActivePage] = useState(0);
    const [bpLevel, setBpLevel] = useState(1);
    const [bpExp, setBpExp] = useState(0);
    const [bpNeed, setBpNeed] = useState(10000);
    const [rewardDetails, setRewardDetails] = useState<Record<number, string>>({});
    const [rewardOpenPass, setRewardOpenPass] = useState<Record<number, boolean>>({});
    const [rewardRequiresPass, setRewardRequiresPass] = useState<Record<number, boolean>>({});
    const [rewardMeta, setRewardMeta] = useState<Record<number, RewardMeta>>({});
    const [requestedDetails, setRequestedDetails] = useState<Record<number, boolean>>({});

    const closeLocal = useCallback(() => {
        setVisible(false);
        window.cef.set_focus(false);
    }, []);

    const selectedReward = useMemo(() => rewards[selectedIndex] ?? null, [rewards, selectedIndex]);
    const pageCount = useMemo(() => Math.max(1, Math.ceil(rewards.length / ITEMS_PER_PAGE)), [rewards.length]);
    const pageRewards = useMemo(() => {
        const start = activePage * ITEMS_PER_PAGE;
        return rewards.slice(start, start + ITEMS_PER_PAGE);
    }, [activePage, rewards]);

    const seasonPercent = useMemo(() => {
        if (bpNeed <= 0) return 0;
        return Math.max(0, Math.min(100, Math.round((bpExp / bpNeed) * 100)));
    }, [bpExp, bpNeed]);
    const selectedDetail = useMemo(() => {
        if (!selectedReward) return '';
        return rewardDetails[selectedReward.index] || '';
    }, [rewardDetails, selectedReward]);
    const selectedDetailLines = useMemo(() => {
        if (!selectedDetail) return [];
        return parseDetailLines(selectedDetail);
    }, [selectedDetail]);
    const selectedDetailDensity = useMemo(() => {
        const visibleLines = selectedDetailLines.filter((line) => !line.isEmpty).length;
        const textLength = stripColorCodes(selectedDetail).length;

        if (visibleLines >= 7 || textLength >= 250) return 'tight';
        if (visibleLines >= 5 || textLength >= 170) return 'compact';
        return 'normal';
    }, [selectedDetail, selectedDetailLines]);
    const selectedOpenPass = useMemo(() => {
        if (!selectedReward) return false;
        return Boolean(rewardOpenPass[selectedReward.index]);
    }, [rewardOpenPass, selectedReward]);
    const selectedRewardMeta = useMemo(() => {
        if (!selectedReward) return undefined;
        return rewardMeta[selectedReward.index];
    }, [rewardMeta, selectedReward]);
    const showPrimaryAction = Boolean(selectedReward) && (selectedReward?.state === 'claim' || selectedOpenPass);
    const primaryActionLabel = useMemo(() => {
        if (!selectedReward) return '';
        if (selectedReward.state === 'claim') {
            return getPrimaryLabel(selectedReward.state, button1);
        }
        if (selectedOpenPass) return 'ADV PASS';
        return '';
    }, [button1, selectedOpenPass, selectedReward]);

    const changePage = useCallback((nextPage: number) => {
        const maxPage = Math.max(0, pageCount - 1);
        const clampedPage = clamp(nextPage, 0, maxPage);
        setActivePage(clampedPage);

        setSelectedIndex((current) => {
            const start = clampedPage * ITEMS_PER_PAGE;
            const end = start + ITEMS_PER_PAGE;
            if (current >= start && current < end) return current;
            return clamp(start, 0, Math.max(0, rewards.length - 1));
        });
    }, [pageCount, rewards.length]);

    const selectByIndex = useCallback((nextIndex: number) => {
        if (!rewards.length) return;
        const clampedIndex = clamp(nextIndex, 0, rewards.length - 1);
        setSelectedIndex(clampedIndex);
        setActivePage(Math.floor(clampedIndex / ITEMS_PER_PAGE));
    }, [rewards.length]);

    const sendResponse = useCallback((response: number) => {
        if (!visible) return;
        const selected = rewards[selectedIndex] ?? null;
        if (response === 1 && selected && selected.state !== 'claim' && !rewardOpenPass[selected.index]) return;
        const listitem = selected ? selected.index : 0;
        const input = selected ? `${selected.level}. ${selected.title}` : '';
        window.cef.emit('cef:dialogResponse', response, listitem, input);
        closeLocal();
    }, [closeLocal, rewardOpenPass, rewards, selectedIndex, visible]);

    const handleDetailPayload = useCallback((data: any) => {
        const payload = parsePayload<BpDetailPayload>(data);
        if (!payload || typeof payload.index !== 'number') return;

        const rawText = payload.text || '';
        const nextText = filterDetailText(rawText);
        const nextOpenPass = Boolean(payload.openPass);
        const nextRequiresPass = detectPassRequirement(rawText);
        const nextMeta = typeof payload.rewardType === 'number' && typeof payload.rewardDetails === 'number'
            ? {
                rewardType: payload.rewardType,
                rewardDetails: payload.rewardDetails,
                previewSkinId: typeof payload.previewSkinId === 'number' ? payload.previewSkinId : 0,
            }
            : null;

        setRewardDetails((current) => {
            if (current[payload.index!] === nextText) return current;
            return { ...current, [payload.index!]: nextText };
        });
        setRewardOpenPass((current) => {
            if (current[payload.index!] === nextOpenPass) return current;
            return { ...current, [payload.index!]: nextOpenPass };
        });
        setRewardRequiresPass((current) => {
            if (current[payload.index!] === nextRequiresPass) return current;
            return { ...current, [payload.index!]: nextRequiresPass };
        });
        if (nextMeta) {
            setRewardMeta((current) => {
                const currentMeta = current[payload.index!];
                if (
                    currentMeta &&
                    currentMeta.rewardType === nextMeta.rewardType &&
                    currentMeta.rewardDetails === nextMeta.rewardDetails &&
                    currentMeta.previewSkinId === nextMeta.previewSkinId
                ) {
                    return current;
                }
                return { ...current, [payload.index!]: nextMeta };
            });
        }
    }, []);

    const showBattlePass = useCallback((data: any) => {
        const payload = parsePayload<BpPayload>(data);
        if (!payload) return;

        if (!payload.show) {
            closeLocal();
            return;
        }

        const nextRewards = parseRewards(payload.text || '');
        if (!nextRewards.length) {
            closeLocal();
            return;
        }

        const nextLevel = typeof payload.level === 'number' && payload.level > 0 ? payload.level : 1;
        const defaultSelected = nextRewards.findIndex((reward) => reward.level === nextLevel);
        const nextSelectedIndex = defaultSelected >= 0 ? defaultSelected : 0;

        setCaption(titleFromCaption(payload.caption || 'BATTLE PASS') || 'BATTLE PASS');
        setDaysLeft(typeof payload.days === 'number' ? payload.days : parseDaysFromCaption(payload.caption || ''));
        setButton1(stripColorCodes(payload.button1 || 'Выбрать'));
        setButton2(stripColorCodes(payload.button2 || 'Закрыть'));
        setRewards(nextRewards);
        setSelectedIndex(nextSelectedIndex);
        setActivePage(Math.floor(nextSelectedIndex / ITEMS_PER_PAGE));
        setBpLevel(nextLevel);
        setBpExp(typeof payload.exp === 'number' ? payload.exp : 0);
        setBpNeed(typeof payload.need === 'number' && payload.need > 0 ? payload.need : 10000);
        setRewardDetails({});
        setRewardOpenPass({});
        setRewardRequiresPass({});
        setRewardMeta({});
        setRequestedDetails({});

        setVisible(true);
        window.cef.set_focus(true);
    }, [closeLocal]);

    useEffect(() => {
        const unsubscribeBp = emitter.subscribe(E_GET_BP, (data: any) => showBattlePass(data));
        const unsubscribeDetail = emitter.subscribe(E_GET_BP_DETAIL, (data: any) => handleDetailPayload(data));
        const unsubscribeForceClose = emitter.subscribe(E_FORCE_DIALOG_CLOSE, () => closeLocal());
        const unsubscribeDialog = emitter.subscribe(E_GET_DIALOG, () => {
            if (visible) closeLocal();
        });

        return () => {
            unsubscribeBp();
            unsubscribeDetail();
            unsubscribeForceClose();
            unsubscribeDialog();
        };
    }, [closeLocal, handleDetailPayload, showBattlePass, visible]);

    useEffect(() => {
        if (!visible || !pageRewards.length) return;

        const missingIndexes = pageRewards
            .map((reward) => reward.index)
            .filter((index) => !requestedDetails[index]);

        if (!missingIndexes.length) return;

        setRequestedDetails((current) => {
            const next = { ...current };
            missingIndexes.forEach((index) => {
                next[index] = true;
            });
            return next;
        });

        missingIndexes.forEach((index) => {
            window.cef.emit('cef:bpDetailRequest', index);
        });
    }, [pageRewards, requestedDetails, visible]);

    useEffect(() => {
        if (!visible) return;

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.repeat) return;
            if (event.ctrlKey || event.altKey || event.metaKey) return;

            switch (event.key) {
                case 'Escape':
                    event.preventDefault();
                    sendResponse(0);
                    return;
                case 'Enter':
                    event.preventDefault();
                    sendResponse(1);
                    return;
                case 'ArrowRight':
                    event.preventDefault();
                    selectByIndex(selectedIndex + 1);
                    return;
                case 'ArrowLeft':
                    event.preventDefault();
                    selectByIndex(selectedIndex - 1);
                    return;
                case 'PageDown':
                    event.preventDefault();
                    changePage(activePage + 1);
                    return;
                case 'PageUp':
                    event.preventDefault();
                    changePage(activePage - 1);
                    return;
                default:
                    return;
            }
        };

        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [activePage, changePage, rewards.length, selectByIndex, selectedIndex, sendResponse, visible]);

    if (!visible) return null;

    return (
        <div className="bp-root">
            <div className="bp-backdrop" onClick={() => sendResponse(0)} />
            <div className="bp-shell">
                <div className="bp-header">
                    <h1>{caption.length > 0 ? caption : 'BATTLE PASS'}</h1>
                    <div className="bp-days">Осталось дней: <span>{daysLeft}</span></div>
                </div>

                <div className="bp-season-progress">
                    <div className="bp-season-top">
                        <span className="bp-season-label">СЕЗОННЫЙ ПРОГРЕСС • УРОВЕНЬ {bpLevel}</span>
                        <span className="bp-season-value">УРОВЕНЬ {bpLevel} • {bpExp}/{bpNeed}</span>
                    </div>
                    <div className="bp-season-track">
                        <div className="bp-season-fill" style={{ width: `${seasonPercent}%` }} />
                    </div>
                </div>

                <div className="bp-content">
                    <div className="bp-selected">
                        <div className="bp-selected-image">
                            {selectedReward ? <img src={getCardImage(selectedReward, selectedRewardMeta)} alt="reward" /> : null}
                        </div>
                        <div className="bp-selected-info">
                            {selectedDetail ? (
                                <div className={`bp-selected-detail density-${selectedDetailDensity}`}>
                                    {selectedDetailLines.map((line, index) => (
                                        <div
                                            key={index}
                                            className={`bp-selected-detail-line ${line.isEmpty ? 'is-empty' : ''}`}
                                        >
                                            {line.segments.map((segment, segmentIndex) => (
                                                <span key={segmentIndex} style={{ color: segment.color }}>
                                                    {segment.text}
                                                </span>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <>
                                    <div className="bp-selected-title">{selectedReward?.title || 'Награда сезона'}</div>
                                    <div className={`bp-badge state-${selectedReward?.state || 'locked'}`}>
                                        {selectedReward ? getStateBadge(selectedReward.state, selectedReward.status) : 'ЗАКРЫТО'}
                                    </div>
                                    <div className="bp-selected-points">{selectedReward?.points || `0 из ${bpNeed}`}</div>
                                    <div className="bp-selected-placeholder">Загрузка подробной информации...</div>
                                </>
                            )}
                        </div>
                    </div>

                    <div className="bp-actions">
                        <div className="bp-actions-title">СТАТУС НАГРАДЫ</div>
                        <div className={`bp-actions-state state-${selectedReward?.state || 'locked'}`}>
                            {selectedReward ? getStateBadge(selectedReward.state, selectedReward.status) : 'ЗАКРЫТО'}
                        </div>
                        {showPrimaryAction ? (
                            <button className="bp-btn bp-btn-primary" type="button" onClick={() => sendResponse(1)}>
                                {primaryActionLabel}
                            </button>
                        ) : (
                            <div className="bp-btn bp-btn-placeholder" aria-hidden="true" />
                        )}
                        <button className="bp-btn bp-btn-secondary" type="button" onClick={() => sendResponse(0)}>
                            {button2.length > 0 ? button2.toUpperCase() : 'ЗАКРЫТЬ'}
                        </button>
                    </div>
                </div>

                <div className="bp-track-wrapper">
                    <div className="bp-track">
                        {pageRewards.map((reward, localIndex) => {
                            const globalIndex = activePage * ITEMS_PER_PAGE + localIndex;
                            const hasPassTag = Boolean(rewardRequiresPass[reward.index]) || reward.state === 'vip';
                            return (
                            <button
                                key={`${reward.level}-${reward.index}`}
                                className={`bp-card state-${reward.state} ${selectedIndex === globalIndex ? 'is-selected' : ''}`}
                                type="button"
                                data-row={reward.index}
                                onClick={() => selectByIndex(globalIndex)}
                                onDoubleClick={() => sendResponse(1)}
                            >
                                {hasPassTag ? <div className="bp-card-pass-tag">ADV PASS</div> : null}
                                <div className="bp-card-level">
                                    <span className="bp-card-level-label">Уровень</span>
                                    <span className="bp-card-level-number">{reward.level}</span>
                                </div>
                                <div className="bp-card-image">
                                    <img src={getCardImage(reward, rewardMeta[reward.index])} alt={reward.title} />
                                </div>
                                <div className="bp-card-title">{reward.title}</div>
                                <div className="bp-card-status">{reward.status}</div>
                                <div className="bp-card-track">
                                    <div style={{ width: `${reward.percent}%` }} />
                                </div>
                                <div className="bp-card-points">{reward.points}</div>
                            </button>
                        )})}
                    </div>

                    <div className="bp-pagination">
                        <button
                            className="bp-page-nav"
                            type="button"
                            onClick={() => changePage(activePage - 1)}
                            disabled={activePage === 0}
                        >
                            ‹
                        </button>
                        <div className="bp-page-strip">
                            {Array.from({ length: pageCount }, (_, index) => (
                                <button
                                    key={index}
                                    className={`bp-page-dot ${activePage === index ? 'is-active' : ''}`}
                                    type="button"
                                    onClick={() => changePage(index)}
                                >
                                    {index + 1}
                                </button>
                            ))}
                        </div>
                        <button
                            className="bp-page-nav"
                            type="button"
                            onClick={() => changePage(activePage + 1)}
                            disabled={activePage >= pageCount - 1}
                        >
                            ›
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BattlePass;
